import { useCallback } from 'react';
import {
	Button,
	ButtonBorderRadius,
	ButtonColors,
	ButtonHeight,
} from '~/components/ui/Button';
import { ScrollContainer } from '~/components/ui/ScrollContainer';
import { Tooltip } from '~/components/ui/Tooltip';
import { rage, useObservable } from '~/rage';
import {
	ANTICHEAT_REASON_LABELS,
	formatAnticheatDetectionDetails,
	formatTime,
} from '~/shared';
import { useVisibleRequestIds } from '../../useVisibleRequestIds';
import {
	StyledButtonCell,
	StyledButtonText,
	StyledButtonWrapper,
	StyledContainer,
	StyledHeader,
	StyledHeaderCell,
	StyledIdentifierCell,
	StyledList,
	StyledNameCell,
	StyledNumberCell,
	StyledOnlineDot,
	StyledPlayerName,
	StyledReasonCell,
	StyledRow,
	StyledTimeCell,
} from './AnticheatTab.styled';

const renderTime = (time: number) =>
	formatTime(time, '{hh}:{mm}:{ss}', { useMoscowTimezone: true });

const AnticheatTab = () => {
	const requests = useObservable(rage.admin.getAnticheatRequests());
	const markAnticheatRequestsViewed = useCallback((requestIds: string[]) => {
		return rage.admin.markAnticheatRequestsViewed(requestIds);
	}, []);
	const { getRowRef, scrollContainerRef } = useVisibleRequestIds(
		requests,
		markAnticheatRequestsViewed,
	);

	return (
		<StyledContainer>
			<StyledHeader>
				<StyledHeaderCell>#</StyledHeaderCell>
				<StyledHeaderCell $center>Никнейм</StyledHeaderCell>
				<StyledHeaderCell $center>ID</StyledHeaderCell>
				<StyledHeaderCell $center>UID</StyledHeaderCell>
				<StyledHeaderCell $center>Причина</StyledHeaderCell>
				<StyledHeaderCell $center>Время</StyledHeaderCell>
			</StyledHeader>
			<ScrollContainer height={470} scrollContainerRef={scrollContainerRef}>
				<StyledList>
					{requests.map((request) => (
						<StyledRow key={request.id} ref={getRowRef(request.id)}>
							<StyledNumberCell>{request.requestNumber}</StyledNumberCell>
							<StyledNameCell>
								{request.online && <StyledOnlineDot />}
								<StyledPlayerName>{request.playerName}</StyledPlayerName>
							</StyledNameCell>
							<StyledIdentifierCell>{request.characterId}</StyledIdentifierCell>
							<StyledIdentifierCell>
								{request.accountId ?? '—'}
							</StyledIdentifierCell>
							<StyledReasonCell>
								<Tooltip
									content={
										formatAnticheatDetectionDetails(
											request.reason,
											request.details,
										) || 'Дополнительные данные не переданы'
									}
								>
									{ANTICHEAT_REASON_LABELS[request.reason]}
								</Tooltip>
							</StyledReasonCell>
							<StyledTimeCell>{renderTime(request.time)}</StyledTimeCell>
							<StyledButtonCell>
								<StyledButtonWrapper>
									<Button
										color={ButtonColors.Select2}
										height={ButtonHeight.Height30}
										borderRadius={ButtonBorderRadius.Radius5}
										disabled={!request.online}
										onClick={() =>
											rage.admin.spectateAnticheatRequest(request.characterId)
										}
									>
										<StyledButtonText>Следить</StyledButtonText>
									</Button>
								</StyledButtonWrapper>
							</StyledButtonCell>
						</StyledRow>
					))}
				</StyledList>
			</ScrollContainer>
		</StyledContainer>
	);
};

export { AnticheatTab };
